import React, {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {JoinForm} from "./demo";
import {CharacterCreator} from "./character";
import {DrawPhase} from "./draw";
import {BumpPhase} from "./bump";
import {GuessPhase} from "./guess";
import {RevealPhase} from "./reveal";
import {InterviewPhase} from "./interview";
import {RoastPhase} from "./roast";
import {FlyStage} from "./stage";

// /demo-preview/tour: every kickoff screen in order with stand-in data, the server answered by a fake, for
// polishing the whole flow without playing a game. Next/Back step through.
const PORTRAIT = '/demo-preview/portrait.webp';
const ME = {token: 'me', name: 'Tessa', nickname: 'Blister Capitalist', portrait_url: PORTRAIT, character_status: 'ready'};
const PARTNER = {token: 'cole', name: 'Cole', nickname: 'Barnacle Baron', portrait_url: PORTRAIT};

// A rough sailboat, in the drawing recording format.
const SAILBOAT = JSON.stringify({v: 1, w: 500, h: 500, events: [
    {t: 'stroke', ts: 0, c: '#FFFFFF', s: 10, pts: [[90, 340], [410, 340], [360, 400], [140, 400], [90, 340]]},
    {t: 'stroke', ts: 400, c: '#FFFFFF', s: 10, pts: [[250, 340], [250, 90]]},
    {t: 'stroke', ts: 800, c: '#EE204D', s: 10, pts: [[250, 100], [380, 310], [250, 310]]},
    {t: 'stroke', ts: 1200, c: '#1F75FE', s: 10, pts: [[40, 440], [120, 420], [200, 445], [280, 420], [360, 445], [460, 425]]},
]});

const VERDICT = 'Tessa, the Blister Capitalist. You ran a marathon for money and drew a medal that Cole mistook for a frisbee. Your interview answers suggest a woman who has never once read the fine print.\n\n' +
    'Cole, the Barnacle Baron. You sail a 140 year old boat and confessed the hull is failing. Tessa saw your sailboat and guessed a shoe. I agree with her.\n\n' +
    'Two humans, one party, zero self-preservation instincts between you. Dismissed.';

const API = {
    '/time': () => ({time: Date.now()}),
    '/demo/join': () => ({token: 'me', is_host: false}),
    '/demo/character': () => ({ok: true}),
    '/demo/my-draw': () => ({word: 'marathon medal', recording: null}),
    '/demo/save-drawing': () => ({ok: true}),
    '/demo/drawings-to-guess': () => ({drawings: [{id: 1, recording: SAILBOAT, guessed: false}]}),
    '/demo/guess': () => ({ok: true}),
    '/demo/my-result': () => ({results: [{word: 'sailboat', guess: 'shoe', score: 12, reaction: 'Way off!', scored: true}],
        drawing: {word: 'marathon medal', guessed: 'frisbee', score: 40, scored: true}, round_points: 92}),
    '/demo/interview': () => ({partner: PARTNER, questions: [
        {question: "What's actively failing on this boat?", options: ['The hull, probably', 'Something wooden', "I don't ask questions"]},
        {question: 'Your pirate voice around tourists?', options: ['Full arrrgh, no shame', 'Subtle nautical lilt', 'I am the tourist']},
    ], answers: []}),
    '/demo/answer': () => ({ok: true}),
    '/demo/bump': () => ({queued: true}),
    '/demo/pair': () => ({ok: true}),
    '/demo/roast': () => ({status: 'ready', text: VERDICT, audio_url: null, timing: null, host_name: 'Cole'}),
};

const useFakeServer = () => {
    useEffect(() => {
        const real = window.fetch;
        window.fetch = (url, opts) => {
            const path = String(url).split('?')[0];
            const fake = API[path];
            if (!fake) return real(url, opts);
            return Promise.resolve(new Response(JSON.stringify(fake()), {status: 200, headers: {'Content-Type': 'application/json'}}));
        };
        return () => { window.fetch = real; };
    }, []);
};

// `t0`: when this screen opened, so its fake deadline stays put however often the tour redraws.
const state = (phase, extra = {}, t0 = Date.now()) => ({
    code: 'tour', mode: 'kickoff', phase, round: 1, rounds_total: 1, is_host: true, all_ready: true,
    server_time: Date.now(), phase_ends_at: phase === 'interview' ? null : t0 + (phase === 'bump' ? 15000 : phase === 'reveal' ? 10000 : 30000),
    you: {...ME, partner: PARTNER, ...extra}, players: [{...ME, paired: false}, {...PARTNER, paired: false}],
});

// Character building with the data arriving on a realistic schedule: reaction ~2s, nickname ~3s, portrait ~9s.
const Building = () => {
    const [you, setYou] = useState({name: 'Tessa', character_status: 'processing'});
    useEffect(() => {
        const t = [
            setTimeout(() => setYou(y => ({...y, zed_reaction: 'A marathon. Untrained. For money. Noted.'})), 2000),
            setTimeout(() => setYou(y => ({...y, nickname: 'Blister Capitalist'})), 3000),
            setTimeout(() => setYou(y => ({...y, character_status: 'ready', portrait_url: PORTRAIT})), 9000),
        ];
        return () => t.forEach(clearTimeout);
    }, []);
    return <CharacterCreator you={you} qr={null} />;
};

const STEPS = [
    ['Join', (next, history, t0) => <JoinForm code="tour" onJoined={next} />],
    ['Building', (next, history, t0) => <Building />],
    ['Draw', (next, history, t0) => <DrawPhase state={state('draw', {}, t0)} offset={0} />],
    ['Bump', (next, history, t0) => <BumpPhase state={state('bump', {partner: null}, t0)} offset={0} />],
    ['Paired', (next, history, t0) => <BumpPhase state={state('bump', {pair_line: 'His hull is failing. Your knees are next.'}, t0)} offset={0} />],
    ['Guess', (next, history, t0) => <GuessPhase state={state('guess', {}, t0)} offset={0} />],
    ['Reveal', (next, history, t0) => <RevealPhase state={state('reveal', {}, t0)} offset={0} />],
    ['Interview', (next, history, t0) => <InterviewPhase state={state('interview', {}, t0)} offset={0} />],
    ['Verdict', (next, history, t0) => <RoastPhase state={state('roast', {}, t0)} history={history} />],
];

export const Tour = () => {
    useFakeServer();
    const history = useHistory();
    const [step, setStep] = useState({i: 0, t0: Date.now()});
    const go = (delta) => {
        const to = Math.max(0, Math.min(STEPS.length - 1, step.i + delta));
        if (to !== step.i) setStep({i: to, t0: Date.now()});
    };
    const next = () => go(1);
    const back = () => go(-1);
    const [name, render] = STEPS[step.i];
    return <>
        <FlyStage screenKey={step.i}>{render(next, history, step.t0)}</FlyStage>
        <div style={{position: 'fixed', right: 10, bottom: 'calc(10px + env(safe-area-inset-bottom))', zIndex: 50, display: 'flex', gap: 6,
            alignItems: 'center', background: 'rgba(0,0,0,0.7)', borderRadius: 20, padding: '4px 6px', fontSize: 13, color: '#aaa'}}>
            <button onClick={back} style={tourBtn}>◀</button>
            <span>{step.i + 1}/{STEPS.length} {name}</span>
            <button onClick={next} style={tourBtn}>▶</button>
        </div>
    </>;
};

const tourBtn = {background: '#333', color: '#fff', border: 0, borderRadius: 14, width: 28, height: 28, fontSize: 13, cursor: 'pointer'};
